import { useEffect, useRef } from 'react';
import { FormProvider } from 'react-hook-form';
import { useParams } from 'react-router';
import { Button, Center, Column, CustomSpinner, Row, Text } from '~/commonComponents';
import {
  AutoSaveStatus,
  GeneralErrorMessage,
  InfoSection,
  JobDetailsFormMapper,
  useJobDetailsForm,
} from '../JobDetailsForm';
import { JobDescriptionForm } from '../JobDetailsForm/components/JobDescription';
import {
  CreatePermanentJobPostingFormSection,
  CreatePermanentJobPostingSteps,
  TCreatePermanentJobPostingPageRoutes,
} from './Types';
import {
  useAutoSaveDraftJobPost,
  useAutoScrollToSection,
  useDraftJobPost,
  useSaveJobPost,
} from './hooks';

export const JobDescriptionPage = () => {
  const { id } = useParams<TCreatePermanentJobPostingPageRoutes>();
  const { draft, isLoading } = useDraftJobPost(id);

  const jobDescriptionRef = useRef<HTMLDivElement>(null);

  const controller = useJobDetailsForm({
    initialValues: draft,
  });

  useEffect(() => {
    if (draft) {
      controller.reset(JobDetailsFormMapper.fromEntity(draft));
    }
  }, [draft, controller]);

  useAutoScrollToSection({
    sectionRefs: {
      [CreatePermanentJobPostingFormSection.JobDescription]: jobDescriptionRef,
    },
    isReady: !isLoading && !!draft,
  });

  const { isPending, hasFormErrors, onSave } = useSaveJobPost(controller, id, {
    targetStep: CreatePermanentJobPostingSteps.ReviewAndPayment,
    triggers: ['jobDescription'],
  });
  const {
    isLoading: isAutoSaveLoading,
    isShowSavedMessage,
    lastSavedAt,
  } = useAutoSaveDraftJobPost(controller, id);

  if (isLoading) {
    return (
      <Center marginBlock={48}>
        <CustomSpinner isLoading size={24} />
      </Center>
    );
  }

  return (
    <>
      <AutoSaveStatus visible={isShowSavedMessage} date={lastSavedAt} />
      <Column width="60%" alignSelf="center">
        <Text bold fontSize={22} marginBottom={14} marginLeft={24}>
          Last Step: Job Description
        </Text>
        {hasFormErrors && <GeneralErrorMessage />}
        <FormProvider {...controller}>
          <Column gap={16}>
            <InfoSection
              icon="document-solid"
              ref={jobDescriptionRef}
              required
              title="Job Description"
            >
              <JobDescriptionForm
                control={controller.control}
                errors={controller.formState.errors}
              />
            </InfoSection>
          </Column>
        </FormProvider>
        <Row justifyContent="center" marginTop={24}>
          <Button text="Next" onClick={onSave} isLoading={isPending || isAutoSaveLoading} />
        </Row>
      </Column>
    </>
  );
};
